import { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { X, Send, Sparkles, Trash2, Loader2 } from 'lucide-react';
import { type ChatMessage, initiateConversation, sendMessage, loadChatHistory, saveChatHistory, clearChatHistory } from '../utils/aiAgent';
interface Props {
  onClose: () => void;
}
function uid(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}
export function AiChatSidebar({
  onClose
}: Props) {
  const stored = loadChatHistory();
  const [messages, setMessages] = useState<ChatMessage[]>(stored.messages);
  const [conversationId, setConversationId] = useState<string | null>(stored.conversationId);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isInitializing, setIsInitializing] = useState(!stored.conversationId && stored.messages.length === 0);
  const [error, setError] = useState<string | null>(null);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const streamingMsgIdRef = useRef<string | null>(null);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);

  // Persist to localStorage on every change
  useEffect(() => {
    saveChatHistory(conversationId, messages);
  }, [conversationId, messages]);

  // Initiate conversation on first open (no stored conversation)
  useEffect(() => {
    if (conversationId || messages.length > 0) {
      setIsInitializing(false);
      return;
    }
    const controller = new AbortController();
    setIsInitializing(true);
    initiateConversation(evt => {
      if (evt.event === 'init') return;
      try {
        const parsed = JSON.parse(evt.data);
        if (parsed.message) {
          const greeting: ChatMessage = {
            id: uid(),
            role: 'assistant',
            content: parsed.message,
            timestamp: Date.now()
          };
          setMessages([greeting]);
        }
      } catch {/* ignore parse errors */}
    }, controller.signal).then(id => {
      if (id) setConversationId(id);
    }).catch(err => {
      if (err.name !== 'AbortError') {
        setError('Failed to connect to AI assistant.');
      }
    }).finally(() => setIsInitializing(false));
    return () => controller.abort();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const suggestions = ['Find opportunity related to Granger Hertzog', 'Find all in progress requests assigned to XYZ  .', 'Recent updates on “XYZ” request', 'Find gnarr of XYZ'];
  const handleSend = useCallback(async () => {
    const text = input.trim();
    if (!text || isStreaming) return;
    const userMsg: ChatMessage = {
      id: uid(),
      role: 'user',
      content: text,
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setError(null);
    setIsStreaming(true);
    setHasUserInteracted(true);

    // Create placeholder for assistant response
    const assistantId = uid();
    streamingMsgIdRef.current = assistantId;
    const assistantMsg: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      timestamp: Date.now()
    };
    setMessages(prev => [...prev, assistantMsg]);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      if (conversationId) {
        await sendMessage(conversationId, text, evt => {
          if (evt.event === 'init') return;
          try {
            const parsed = JSON.parse(evt.data);
            if (parsed.message) {
              setMessages(prev => prev.map(m => m.id === assistantId ? {
                ...m,
                content: m.content + parsed.message
              } : m));
            }
          } catch {/* ignore */}
        }, controller.signal);
      } else {
        // No conversation yet — initiate with the user's query
        const id = await initiateConversation(evt => {
          if (evt.event === 'init') return;
          try {
            const parsed = JSON.parse(evt.data);
            if (parsed.message) {
              setMessages(prev => prev.map(m => m.id === assistantId ? {
                ...m,
                content: m.content + parsed.message
              } : m));
            }
          } catch {/* ignore */}
        }, controller.signal);
        if (id) setConversationId(id);
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError('Failed to get response. Please try again.');
      // Remove empty assistant message on error
      setMessages(prev => prev.filter(m => m.id !== assistantId));
    } finally {
      setIsStreaming(false);
      streamingMsgIdRef.current = null;
      abortRef.current = null;
      inputRef.current?.focus();
    }
  }, [input, isStreaming, conversationId]);
  const handleClearChat = () => {
    clearChatHistory();
    setConversationId(null);
    setMessages([]);
    setError(null);
    setHasUserInteracted(false);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  return <div className="ai-sidebar ai-sidebar-enter" onClick={e => e.stopPropagation()} data-id="element-61" data-mp="34fb5sphh7ozr">
      {/* Header */}
      <div className="ai-sidebar-header" data-id="element-62" data-mp="3s4n9xaxy56v7">
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }} data-id="element-63" data-mp="3s3kin1wpp2er">
          <Sparkles size={16} style={{
          color: '#7c3aed'
        }} data-id="element-64" data-mp="rj70c330wg4" />
          <span style={{
          fontWeight: 700,
          fontSize: 14,
          color: '#1a1a2e'
        }} data-id="element-65" data-mp="amgavozbmdpi">AI OnDemand</span>
        </div>
        <div style={{
        display: 'flex',
        gap: 4
      }} data-id="element-66" data-mp="5nmy5yruyqqe">
          <button onClick={handleClearChat} title="New conversation" className="ai-icon-btn" data-id="element-67" data-mp="5nwo5mcwkxw3">
            <Trash2 size={14} data-id="element-68" data-mp="mpgroeoa0py" />
          </button>
          <button onClick={onClose} title="Close" className="ai-icon-btn" data-id="element-69" data-mp="3sscvp0684pk7">
            <X size={16} data-id="element-70" data-mp="3su5w3xq8udjr" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="ai-messages" data-id="element-71" data-mp="3nv2xav0ux3k8">
        {isInitializing && <div style={{
        display: 'flex',
        justifyContent: 'center',
        padding: 20
      }} data-id="element-72" data-mp="3j1hxqyjm81ix">
            <Loader2 size={20} className="ai-spin" style={{
          color: '#7c3aed'
        }} data-id="element-73" data-mp="3e2f5keoqhpsd" />
          </div>}

        {messages.map(msg => <div key={msg.id} className={`ai-msg ai-msg-${msg.role}`} data-id="element-74" data-mp="395wyh85xlfcv">
            {msg.role === 'assistant' && <div className="ai-msg-avatar" data-id="element-75" data-mp="354bfd52t04u8">
                <Sparkles size={12} data-id="element-76" data-mp="304gp93qurja8" />
              </div>}
            <div className="ai-msg-bubble" data-id="element-77" data-mp="1flmzurp2ktac">
              {msg.role === 'assistant' ? <div className="ai-md" data-id="element-78" data-mp="1kkg1nmuzuvuo">
                  {msg.content ? <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
              a: ({
                children,
                ...props
              }) => <a {...props} target="_blank" rel="noopener noreferrer" data-id="element-80" data-mp="1tlo1i9d5s4qv">{children}</a>
            }} data-id="element-79" data-mp="1kmkg2yqird97">{msg.content}</ReactMarkdown> : msg.id === streamingMsgIdRef.current ? <div className="ai-typing" data-id="element-81" data-mp="10xa1jo2ieyco">
                      <span data-id="element-82" data-mp="15vjxi0w262zc" /><span data-id="element-83" data-mp="15vtptv4ksm87" /><span data-id="element-84" data-mp="15vtmpm9a463z" />
                    </div> : null}
                </div> : <span data-id="element-85" data-mp="1anbqsj6lmmt6">{msg.content}</span>}
            </div>
          </div>)}

        {error && <div className="ai-msg ai-msg-error" data-id="element-86" data-mp="d6czd30infem">
            <span data-id="element-87" data-mp="i5po5tay6wju">{error}</span>
          </div>}

        {!isInitializing && !hasUserInteracted && !error && !input && <div className="ai-suggestions" data-id="element-88" data-mp="37p3yyevue42x">
            {suggestions.map(s => <button key={s} className="ai-suggestion-chip" onClick={() => {
          setInput(s);
          inputRef.current?.focus();
        }} data-id="element-89" data-mp="32prdnykmd52g">
                {s}
              </button>)}
          </div>}

        <div ref={messagesEndRef} data-id="element-90" data-mp="3qlm0fo0hticd" />
      </div>

      {/* Input */}
      <div className="ai-input-bar" data-id="element-91" data-mp="3loc21iv4lwce">
        <textarea ref={inputRef} className="ai-input" placeholder="Ask anything about your deals..." value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} rows={1} disabled={isStreaming} data-id="element-92" data-mp="3ln0v5a6dw8h2" />
        <button className="ai-send-btn" onClick={() => void handleSend()} disabled={!input.trim() || isStreaming} title="Send message" data-id="element-93" data-mp="d8jxgwdzdgo9">
          {isStreaming ? <Loader2 size={16} className="ai-spin" data-id="element-94" data-mp="rv8vsuwhxnt2" /> : <Send size={16} data-id="element-95" data-mp="wtk6f7um82cc" />}
        </button>
      </div>
    </div>;
}